import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import ContentTrackingService from './backendservices/ContentTrackingService';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';

interface Prompt {
  _id: string;
  title: string;
  content: string;
  category: string;
  technologies?: string[];
  createdBy: {
    _id: string;
    username: string;
    email: string;
  };
  createdAt: string;
}

interface Category {
  _id: string;
  name: string;
  description?: string;
}

interface PromptLibraryProps {
  onShowSnackbar: (message: string, severity: 'success' | 'error') => void;
}

const PromptLibrary: React.FC<PromptLibraryProps> = ({ onShowSnackbar }) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState<Partial<Prompt>>({
    title: '',
    content: '',
    category: 'General',
    technologies: [],
  });
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const userRole = Cookies.get('role');
  const isSuperAdmin = userRole === 'superadmin';

  // Category State
  const [categories, setCategories] = useState<Category[]>([]);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openQuickAddCategoryDialog, setOpenQuickAddCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [quickCategoryName, setQuickCategoryName] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    id: string | null;
  }>({ open: false, id: null });
  const [categoryToDelete, setCategoryToDelete] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPrompts, setTotalPrompts] = useState(0);

  useEffect(() => {
    fetchPrompts();
    fetchCategories();
  }, [page, rowsPerPage]);

  const fetchCategories = async () => {
    try {
      const response = await ContentTrackingService.getAllCategories();
      if (response.status) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Don't show snackbar on init load failure to avoid spam if just starting
    }
  };

  const handleCreateCategory = async (nameOverride?: string) => {
    const nameToCreate = nameOverride || newCategoryName;
    if (!nameToCreate?.trim()) return;

    try {
      const response = await ContentTrackingService.createCategory({ name: nameToCreate });
      if (response.status) {
        onShowSnackbar('Category created successfully', 'success');
        if (!nameOverride) setNewCategoryName(''); // Clear only if using the manage dialog input
        await fetchCategories();
        return response.data; // Return the created category
      }
    } catch (error: any) {
      console.error('Error creating category:', error);
      onShowSnackbar(error.response?.data?.message || 'Failed to create category', 'error');
    }
  };

  const handleDeleteCategory = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete category "${name}"?`)) return;
    try {
      const response = await ContentTrackingService.deleteCategory(id);
      if (response.status) {
        onShowSnackbar('Category deleted successfully', 'success');
        fetchCategories();
        // If filter was set to this category, reset it
        if (categoryFilter === name) {
          setCategoryFilter('All Categories');
        }
      }
    } catch (error: any) {
      console.error('Error deleting category:', error);
      onShowSnackbar(error.response?.data?.message || 'Failed to delete category', 'error');
    }
  };

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const response = await ContentTrackingService.getAllPrompts(page + 1, rowsPerPage);
      if (response.status) {
        setPrompts(response.data);
        setTotalPrompts(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
      onShowSnackbar('Failed to fetch prompts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdatePrompt = async () => {
    if (!currentPrompt.title || !currentPrompt.content) {
      onShowSnackbar('Title and content are required', 'error');
      return;
    }

    try {
      if (editMode && currentPrompt._id) {
        // Update
        const response = await ContentTrackingService.updatePrompt(
          currentPrompt._id,
          currentPrompt
        );
        if (response.status) {
          onShowSnackbar('Prompt updated successfully', 'success');
        }
      } else {
        // Create
        const response = await ContentTrackingService.createPrompt(currentPrompt);
        if (response.status) {
          onShowSnackbar('Prompt created successfully', 'success');
        }
      }
      setOpenDialog(false);
      fetchPrompts();
      resetForm();
    } catch (error) {
      console.error('Error saving prompt:', error);
      onShowSnackbar('Failed to save prompt', 'error');
    }
  };

  // Delete Prompt
  const handleDeletePrompt = (id: string) => {
    setDeleteConfirmation({ open: true, id });
  };

  const confirmDeletePrompt = async () => {
    if (!deleteConfirmation.id) return;
    try {
      await ContentTrackingService.deletePrompt(deleteConfirmation.id);
      setPrompts(prompts.filter((p) => p._id !== deleteConfirmation.id));
      onShowSnackbar('Prompt deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting prompt:', error);
      onShowSnackbar('Failed to delete prompt', 'error');
    } finally {
      setDeleteConfirmation({ open: false, id: null });
    }
  };

  const resetForm = () => {
    setCurrentPrompt({
      title: '',
      content: '',
      category: 'General',
      technologies: [],
    });
    setEditMode(false);
    setViewMode(false);
  };

  const handleCopyToClipboard = (content: string) => {
    navigator.clipboard.writeText(content);
    onShowSnackbar('Prompt copied to clipboard', 'success');
  };

  const filteredPrompts = prompts.filter((prompt) => {
    const matchesSearch = prompt.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === 'All Categories' || prompt.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visiblePrompts = filteredPrompts;

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 700, color: '#6871d1', mb: 1 }}>
            Prompt Library
          </Typography>
          <Typography variant='body2' sx={{ color: '#8f90a6' }}>
            Manage organization AI system prompts
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder='Search current page...'
            size='small'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: '#8f90a6' }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: '#1e1e2f',
                color: '#fff',
                borderRadius: 2,
                width: 300,
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '& input': { color: '#fff' },
              },
            }}
          />
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setOpenDialog(true);
            }}
            sx={{
              backgroundColor: '#6871d1',
              color: '#fff',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
              '&:hover': {
                backgroundColor: '#5a63bb',
              },
            }}
          >
            Add Prompt
          </Button>
          <Button
            variant='outlined'
            startIcon={<SettingsIcon />}
            onClick={() => setOpenCategoryDialog(true)}
            sx={{
              borderColor: '#6871d1',
              color: '#6871d1',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(104, 113, 209, 0.04)',
                borderColor: '#5a63bb',
              },
            }}
          >
            Manage Categories
          </Button>
        </Box>
      </Box>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 3,
          bgcolor: '#2b2c3e', // Darker bg for filter bar
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', color: '#8f90a6', mr: 2 }}>
          <FilterListIcon fontSize='small' sx={{ mr: 1 }} />
          <Typography variant='body2' fontWeight={600}>
            Quick Filters:
          </Typography>
        </Box>
        <Select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          size='small'
          displayEmpty
          sx={{
            minWidth: 150,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            color: '#fff',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: '#8f90a6' },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 250, // Approx 5 items height (48px per item)
                bgcolor: '#2b2c3e',
                color: '#fff',
                '& .MuiMenuItem-root': {
                  '&:hover': { bgcolor: '#1e1e2f' },
                  '&.Mui-selected': { bgcolor: '#6871d1', '&:hover': { bgcolor: '#5a63bb' } },
                },
              },
            },
          }}
        >
          <MenuItem value='All Categories'>All Categories</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat._id} value={cat.name}>
              {cat.name}
            </MenuItem>
          ))}
        </Select>
      </Paper>

      {/* Prompts List */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='h6' sx={{ fontWeight: 700, color: '#fff' }}>
          Available Prompts
        </Typography>
        <Chip
          label={`Total: ${filteredPrompts.length}`}
          size='small'
          sx={{ bgcolor: '#6871d1', color: '#fff' }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <React.Fragment>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {visiblePrompts.map((prompt) => (
              <Paper
                key={prompt._id}
                elevation={0}
                onClick={() => {
                  setCurrentPrompt(prompt);
                  setViewMode(true);
                  setOpenDialog(true);
                }}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: '#1e1e2f',
                  borderLeft: '4px solid #6871d1',
                  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                  borderRight: '1px solid rgba(255, 255, 255, 0.05)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#23234a',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Grid container alignItems='center' spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: 'rgba(104, 113, 209, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6871d1',
                          flexShrink: 0,
                        }}
                      >
                        <ChatBubbleOutlineIcon />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant='subtitle1' sx={{ fontWeight: 700, color: '#fff' }}>
                            {prompt.title}
                          </Typography>
                          <Chip
                            label={prompt.category}
                            size='small'
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: '#2c2d3c',
                              color: '#ddd',
                              border: '1px solid #444',
                            }}
                          />
                          {/* Render technologies/tags as additional chips if available */}
                          {prompt.technologies &&
                            prompt.technologies.map((tech, idx) => (
                              <Chip
                                key={idx}
                                label={tech}
                                size='small'
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: 'rgba(102, 187, 106, 0.1)',
                                  color: '#66bb6a',
                                  border: '1px solid rgba(102, 187, 106, 0.2)',
                                }}
                              />
                            ))}
                        </Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#8f90a6' }}
                        >
                          <Typography
                            variant='caption'
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <PersonIcon sx={{ fontSize: 14 }} />
                            {prompt.createdBy?.username || 'Unknown'}
                          </Typography>
                          <Typography
                            variant='caption'
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            {dayjs(prompt.createdAt).format('MMM DD, YYYY')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Tooltip title='Copy Prompt'>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyToClipboard(prompt.content);
                          }}
                          size='small'
                          sx={{ color: '#8f90a6', '&:hover': { color: '#fff' } }}
                        >
                          <ContentCopyIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Edit'>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentPrompt(prompt);
                            setEditMode(true);
                            setViewMode(false);
                            setOpenDialog(true);
                          }}
                          size='small'
                          sx={{ color: '#8f90a6', '&:hover': { color: '#fff' } }}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      {isSuperAdmin && (
                        <Tooltip title='Delete'>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePrompt(prompt._id);
                            }}
                            size='small'
                            sx={{ color: '#8f90a6', '&:hover': { color: '#f44336' } }}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={totalPrompts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: '#fff',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                color: '#8f90a6',
              },
              '.MuiTablePagination-select': {
                color: '#fff',
              },
              '.MuiTablePagination-actions': {
                color: '#fff',
              },
            }}
          />
        </React.Fragment>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2b2c3e',
            color: '#fff',
            borderRadius: 3,
            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#23234a',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2.5,
            px: 3,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(104, 113, 209, 0.2)',
              p: 0.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6871d1',
            }}
          >
            {viewMode ? (
              <VisibilityIcon fontSize='small' />
            ) : editMode ? (
              <VisibilityIcon fontSize='small' />
            ) : (
              <AddIcon fontSize='small' />
            )}
          </Box>
          {viewMode ? 'Prompt Details' : editMode ? 'Edit Prompt' : 'Create New Prompt'}
        </DialogTitle>
        <DialogContent sx={{ background: '#2b2c3e', px: 3, pb: 2 }}>
          {viewMode ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <Box>
                <Typography variant='caption' sx={{ color: '#8f90a6', mb: 0.5, display: 'block' }}>
                  Title
                </Typography>
                <Typography variant='h6' sx={{ color: '#fff', fontWeight: 600 }}>
                  {currentPrompt.title}
                </Typography>
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant='caption'
                      sx={{ color: '#8f90a6', mb: 0.5, display: 'block' }}
                    >
                      Category
                    </Typography>
                    <Chip
                      label={currentPrompt.category}
                      size='small'
                      sx={{
                        bgcolor: '#1e1e2f',
                        color: '#ddd',
                        border: '1px solid #444',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant='caption'
                      sx={{ color: '#8f90a6', mb: 0.5, display: 'block' }}
                    >
                      Model
                    </Typography>
                    <Typography variant='body2' sx={{ color: '#fff' }}>
                      {currentPrompt.technologies?.join(', ') || '-'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant='caption' sx={{ color: '#8f90a6' }}>
                    Prompt Instructions
                  </Typography>
                  <Button
                    startIcon={<ContentCopyIcon />}
                    size='small'
                    onClick={() => handleCopyToClipboard(currentPrompt.content || '')}
                    sx={{
                      color: '#6871d1',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '&:hover': { bgcolor: 'rgba(104, 113, 209, 0.1)' },
                    }}
                  >
                    Copy
                  </Button>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: '#000',
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    maxHeight: '400px',
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    variant='body2'
                    component='pre'
                    sx={{
                      color: '#00ff00',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      m: 0,
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                    }}
                  >
                    {currentPrompt.content}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <Typography
                  variant='caption'
                  sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
                >
                  Prompt Title *
                </Typography>
                <TextField
                  placeholder='Enter prompt title'
                  fullWidth
                  value={currentPrompt.title}
                  onChange={(e) => setCurrentPrompt({ ...currentPrompt, title: e.target.value })}
                  variant='outlined'
                  InputProps={{
                    sx: {
                      bgcolor: '#1e1e2f',
                      color: '#fff',
                      borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                      height: '48px',
                    },
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant='caption'
                      sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
                    >
                      Category
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={currentPrompt.category}
                      onChange={async (e) => {
                        const selectedValue = e.target.value;
                        if (selectedValue === '__ADD_NEW__') {
                          setOpenQuickAddCategoryDialog(true);
                        } else {
                          setCurrentPrompt({ ...currentPrompt, category: selectedValue });
                        }
                      }}
                      variant='outlined'
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 250,
                              bgcolor: '#1e1e2f',
                              color: '#fff',
                              '& .MuiMenuItem-root': {
                                '&:hover': { bgcolor: '#2b2c3e' },
                                '&.Mui-selected': {
                                  bgcolor: '#6871d1',
                                  '&:hover': { bgcolor: '#5a63bb' },
                                },
                              },
                            },
                          },
                        },
                      }}
                      InputProps={{
                        sx: {
                          bgcolor: '#1e1e2f',
                          color: '#fff',
                          borderRadius: 2,
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                          '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                          '& .MuiSvgIcon-root': { color: '#8f90a6' },
                          height: '48px',
                        },
                      }}
                    >
                      <MenuItem value='General'>None / No Category</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat._id} value={cat.name}>
                          {cat.name}
                        </MenuItem>
                      ))}
                      <MenuItem value='__ADD_NEW__' sx={{ fontWeight: 'bold', color: '#6871d1' }}>
                        + Add Custom Category
                      </MenuItem>
                    </TextField>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant='caption'
                      sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
                    >
                      Model (Optional)
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder='e.g. GPT-4o'
                      value={currentPrompt.technologies?.join(', ') || ''}
                      onChange={(e) =>
                        setCurrentPrompt({
                          ...currentPrompt,
                          technologies: e.target.value.split(',').map((s) => s.trim()),
                        })
                      }
                      variant='outlined'
                      InputProps={{
                        sx: {
                          bgcolor: '#1e1e2f',
                          color: '#fff',
                          borderRadius: 2,
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                          '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                          height: '48px',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box>
                <Typography
                  variant='caption'
                  sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
                >
                  Prompt Instructions *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  value={currentPrompt.content}
                  onChange={(e) => setCurrentPrompt({ ...currentPrompt, content: e.target.value })}
                  InputProps={{
                    sx: {
                      fontFamily: 'monospace',
                      bgcolor: '#000', // Keep pure black for code editor feel
                      color: '#00ff00',
                      borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                      '& .MuiInputBase-input': {
                        color: '#e0e0e0', // Slightly softer white/grey
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                      },
                      p: 2,
                    },
                  }}
                  placeholder='Enter the system instructions...'
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#23234a', p: 3 }}>
          {viewMode ? null : (
            <React.Fragment>
              <Button
                onClick={() => setOpenDialog(false)}
                sx={{
                  backgroundColor: 'transparent',
                  color: '#fff',
                  border: '1px solid #6871d1',
                  py: 1,
                  borderRadius: '8px',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  px: 4,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'rgba(104,113,209,0.1)',
                    border: '1px solid #6871d1',
                  },
                }}
              >
                CANCEL
              </Button>
              <Button
                onClick={handleCreateOrUpdatePrompt}
                variant='contained'
                sx={{
                  backgroundColor: '#6871d1',
                  color: '#fff',
                  py: 1,
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
                  transition: 'all 0.3s ease',
                  fontWeight: 500,
                  px: 4,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: '#5a63bb',
                    boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
                  },
                }}
              >
                {editMode ? 'UPDATE' : 'CREATE'}
              </Button>
            </React.Fragment>
          )}
        </DialogActions>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog
        open={openCategoryDialog}
        onClose={() => setOpenCategoryDialog(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2b2c3e',
            color: '#fff',
            borderRadius: 3,
            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#23234a',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2.5,
            px: 3,
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(104, 113, 209, 0.2)',
              p: 0.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6871d1',
            }}
          >
            <SettingsIcon fontSize='small' />
          </Box>
          Manage Categories
        </DialogTitle>
        <DialogContent sx={{ background: '#2b2c3e', px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='caption'
                sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
              >
                Add New Category
              </Typography>
              <TextField
                placeholder='E.g. Development, Marketing'
                size='small'
                fullWidth
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                InputProps={{
                  sx: {
                    bgcolor: '#1e1e2f',
                    color: '#fff',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                  },
                }}
              />
            </Box>
            <Button
              variant='contained'
              onClick={() => handleCreateCategory()}
              disabled={!newCategoryName.trim()}
              sx={{
                bgcolor: '#6871d1',
                color: '#fff',
                height: 40,
                px: 3,
                borderRadius: 2,
                '&:hover': { bgcolor: '#5a63bb' },
                '&:disabled': {
                  backgroundColor: '#aeb3e9',
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              ADD
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pt: 3,
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography variant='caption' color='error' sx={{ fontWeight: 600, mb: 0.5 }}>
              DANGER ZONE: Remove Category
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Select
                value={categoryToDelete}
                onChange={(e) => setCategoryToDelete(e.target.value)}
                size='small'
                displayEmpty
                fullWidth
                sx={{
                  bgcolor: '#1e1e2f',
                  color: '#fff',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f' },
                  '& .MuiSvgIcon-root': { color: '#8f90a6' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 250,
                      bgcolor: '#1e1e2f',
                      color: '#fff',
                      '& .MuiMenuItem-root': {
                        '&:hover': { bgcolor: '#2b2c3e' },
                        '&.Mui-selected': {
                          bgcolor: '#6871d1',
                          '&:hover': { bgcolor: '#5a63bb' },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value='' disabled sx={{ color: '#8f90a6' }}>
                  Select category to remove
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant='contained'
                color='error'
                onClick={() => {
                  const cat = categories.find((c) => c._id === categoryToDelete);
                  if (cat) handleDeleteCategory(cat._id, cat.name);
                  setCategoryToDelete('');
                }}
                disabled={!categoryToDelete}
                startIcon={<DeleteIcon />}
                sx={{
                  height: 40,
                  borderRadius: 2,
                  px: 2,
                  bgcolor: '#d32f2f',
                  '&:hover': { bgcolor: '#b71c1c' },
                  '&:disabled': { opacity: 0.5, bgcolor: '#ef9a9a' },
                }}
              >
                Remove
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ background: '#23234a', p: 2 }}>
          <Button
            onClick={() => setOpenCategoryDialog(false)}
            sx={{
              color: '#8f90a6',
              '&:hover': { color: '#fff' },
            }}
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Category Dialog */}
      <Dialog
        open={openQuickAddCategoryDialog}
        onClose={() => setOpenQuickAddCategoryDialog(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2b2c3e',
            color: '#fff',
            borderRadius: 3,
            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#23234a',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1rem',
            py: 2,
            px: 3,
          }}
        >
          Add Custom Category
        </DialogTitle>
        <DialogContent sx={{ background: '#2b2c3e', px: 3, pb: 1 }}>
          <Box>
            <Typography
              variant='caption'
              sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
            >
              Category Name
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size='small'
              value={quickCategoryName}
              onChange={(e) => setQuickCategoryName(e.target.value)}
              InputProps={{
                sx: {
                  bgcolor: '#1e1e2f',
                  color: '#fff',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ background: '#23234a', p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}
        >
          <Button
            onClick={() => setOpenQuickAddCategoryDialog(false)}
            sx={{
              color: '#fff',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (quickCategoryName.trim()) {
                const newCat = await handleCreateCategory(quickCategoryName);
                if (newCat) {
                  // Use the new category name immediately
                  setCurrentPrompt((prev) => ({ ...prev, category: newCat.name }));
                }
                setQuickCategoryName('');
                setOpenQuickAddCategoryDialog(false);
              }
            }}
            variant='contained'
            sx={{
              bgcolor: '#6871d1',
              color: '#fff',
              borderRadius: 2,
              '&:hover': { bgcolor: '#5a63bb' },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, id: null })}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e2f',
            color: '#fff',
            borderRadius: 4,
            boxShadow: '0 20px 60px 0 rgba(0,0,0,0.9)',
            border: '1px solid rgba(244, 67, 54, 0.2)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#23234a',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2.5,
            px: 3,
            m: 0,
          }}
        >
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(244, 67, 54, 0.3)',
            }}
          >
            <DeleteIcon sx={{ color: '#f44336', fontSize: 22 }} />
          </Box>
          <Typography
            variant='h6'
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: '1.25rem',
              letterSpacing: 0.3,
            }}
          >
            Delete Prompt
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ background: '#1e1e2f', pt: 3.5, pb: 2, px: 3 }}>
          <Typography
            variant='body1'
            sx={{
              color: '#fff',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              fontWeight: 400,
              mb: 2.5,
            }}
          >
            Are you sure you want to delete this prompt? This action cannot be undone and the prompt
            will be permanently removed from the library.
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              borderRadius: 2,
              border: '1px solid rgba(244, 67, 54, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: '#f44336',
                flexShrink: 0,
              }}
            />
            <Typography
              variant='body2'
              sx={{
                color: '#ffcdd2',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              This action is permanent and cannot be reversed
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            background: '#23234a',
            p: 2.5,
            gap: 1.5,
            m: 0,
          }}
        >
          <Button
            onClick={() => setDeleteConfirmation({ open: false, id: null })}
            sx={{
              backgroundColor: 'transparent',
              color: '#aaa',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              py: 1.25,
              borderRadius: '8px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              px: 3,
              fontSize: '0.95rem',
              textTransform: 'none',
              minWidth: 100,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeletePrompt}
            variant='contained'
            startIcon={<DeleteIcon />}
            sx={{
              backgroundColor: '#f44336',
              color: '#fff',
              py: 1.25,
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
              transition: 'all 0.2s ease',
              fontWeight: 600,
              px: 3,
              fontSize: '0.95rem',
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                backgroundColor: '#d32f2f',
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.5)',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptLibrary;
